import React, { useCallback, useMemo } from "react";
import { useTranslate } from "@avinet/adaptive-ui-translate";
import Icon from "../../ui/Icon";
import { useFormApi } from "../useForm";
import LabeledControl from "../utils/LabeledControl";
import "./List.scss";
export function isItem(t) {
    if (t.value !== undefined)
        return true;
    return false;
}
const emptyArray = [];
/**
 * A form control for displaying a set of options as a list. Supports various automatic styles and display modes using the available translations.
 */
function List({ id, className, langKey, label, langSet = "ListItem", layout, items, groups, multi = false, required = false, disabled = false, itemDescription = false, info = false, noItemTranslation = false, noLabels = false, }) {
    var _a;
    const tItemLabels = useTranslate(langSet);
    const form = useFormApi();
    const { getValue, setValue } = form;
    const value = (_a = getValue(id)) !== null && _a !== void 0 ? _a : (multi ? emptyArray : undefined);
    if (!langKey && typeof id === "string")
        langKey = id;
    const getLabel = useCallback((item) => {
        if (item.label)
            return item.label;
        if (isItem(item)) {
            if (langSet && !noItemTranslation)
                return tItemLabels(item.value.toString());
            return item.value.toString();
        }
        else {
            if (langSet && !noItemTranslation)
                return tItemLabels(item.group);
            return item.group;
        }
    }, [noItemTranslation, langSet, tItemLabels]);
    const normalizedItems = useMemo(() => {
        if (!groups)
            return items;
        return groups.flatMap((group) => [
            group,
            ...items.filter((item) => item.group === group.group),
        ]);
    }, [items, groups]);
    const onValueCheckChange = useCallback((v, checked) => {
        var _a;
        if (multi) {
            const values = (_a = value) !== null && _a !== void 0 ? _a : [];
            if (checked && values.indexOf(v) === -1) {
                setValue(id, [...values, v]);
            }
            else if (!checked && values.indexOf(v) !== -1) {
                const arr = values.filter((value) => value !== v);
                setValue(id, arr.length === 0 ? null : arr);
            }
        }
        else {
            if (checked)
                setValue(id, v);
            else if (!required)
                setValue(id, null);
        }
    }, [id, value, setValue, required, multi]);
    return (React.createElement(LabeledControl, { id: id, langKey: langKey, label: label, info: info, required: required, disabled: disabled, className: `aui-list ${multi ? "multi" : "single"} ${layout !== null && layout !== void 0 ? layout : ""} ${className !== null && className !== void 0 ? className : ""}` },
        React.createElement("div", { className: "list-items" },
            normalizedItems.map((item) => {
                if (isItem(item)) {
                    const label = getLabel(item);
                    const isChecked = multi
                        ? value.indexOf(item.value) !== -1
                        : value === item.value;
                    return (React.createElement("label", { className: "btn list-item " + (isChecked ? "selected" : ""), key: id + "_" + item.value, title: noLabels ? label : undefined },
                        multi && (React.createElement("span", { className: "checkmark" }, isChecked && React.createElement(Icon, { name: "check" }))),
                        item.icon && React.createElement(Icon, { name: item.icon }),
                        item.img && React.createElement("img", { src: item.img, alt: label }),
                        React.createElement("input", { type: multi ? "checkbox" : "radio", value: item.value.toString(), checked: isChecked, onClick: () => {
                                if (isChecked)
                                    onValueCheckChange(item.value, false);
                            }, onChange: (e) => {
                                onValueCheckChange(item.value, e.target.checked);
                            }, disabled: disabled, name: id }),
                        !noLabels && React.createElement("span", null, label)));
                }
                else {
                    const label = getLabel(item);
                    return React.createElement("h4", { key: item.group }, label);
                }
            }),
            React.createElement("input", { "aria-hidden": "true", "aria-disabled": "true", className: "force-required", type: "text", value: multi ? (value.length === 0 ? "" : "filled") : value !== null && value !== void 0 ? value : "", required: required, onChange: (e) => (e.target.value = value), tabIndex: -1, disabled: disabled })),
        itemDescription && (React.createElement("div", { className: "info", dangerouslySetInnerHTML: {
                __html: tItemLabels(value + "Description"),
            } }))));
}
export default List;
